/**
 * Robust viewport height variable initialization.
 *
 * Sets CSS variable `--app-100vh` to the current visual viewport height in pixels,
 * updating on relevant events (resize, orientation change, dynamic browser UI changes).
 *
 * This complements CSS support for `100dvh` and covers older mobile browsers.
 *
 * Also computes `--keyboard-inset` (px) so bottom-fixed UI can sit above the
 * on-screen keyboard. It uses VisualViewport when available to estimate the
 * occluded area by the keyboard.
 */
import { isMobileApp } from "@sharpana/shared/utils/web_environment";

export function initViewportHeightVar(): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    // SSR / non-DOM environment: no-op disposer
    return () => {};
  }

  const root = document.documentElement;
  const vv = window.visualViewport ?? null;

  let raf = 0;
  const timeouts: number[] = [];
  let lastVH = -1;
  let lastInset = -1;

  const computeVH = (): number => {
    try {
      const vvLocal = window.visualViewport ?? null;
      const zooming =
        vvLocal &&
        typeof vvLocal.scale === "number" &&
        Math.abs(vvLocal.scale - 1) > 0.001;
      const raw = Math.floor((vvLocal?.height ?? window.innerHeight) || 0);
      // Freeze during pinch-zoom to avoid breathing layout
      return zooming && lastVH > 0 ? lastVH : raw;
    } catch {
      return lastVH > 0 ? lastVH : Math.floor(window.innerHeight || 0);
    }
  };

  const computeKeyboardInset = (): number => {
    try {
      if (isMobileApp()) return 0; // RN WebView handles insets
      const vvLocal = window.visualViewport;
      if (!vvLocal) return 0;
      // Use layout viewport as baseline; innerHeight can couple to VV on some platforms
      const baseline =
        document.documentElement.clientHeight || window.innerHeight;
      const occluded = Math.max(
        0,
        baseline - Math.round(vvLocal.height + vvLocal.offsetTop)
      );
      return occluded;
    } catch {
      return 0;
    }
  };

  const apply = () => {
    // --app-100vh
    const vh = computeVH();
    if (vh > 0 && vh !== lastVH) {
      root.style.setProperty("--app-100vh", `${vh}px`);
      lastVH = vh;
    }

    // --keyboard-inset
    const inset = computeKeyboardInset();
    if (inset !== lastInset) {
      root.style.setProperty("--keyboard-inset", `${inset}px`);
      lastInset = inset;
    }
  };

  const schedule = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(apply);
  };

  // Initial
  schedule();

  // Listeners (avoid options object for removal symmetry)
  const onResize = schedule;
  const onOrientation = schedule;
  const onVvResize = schedule;
  const onVvScroll = schedule; // offsetTop can change without resize
  const onVvGeometry = schedule; // Safari geometrychange when available

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onOrientation);
  if (vv) {
    vv.addEventListener("resize", onVvResize);
    vv.addEventListener("scroll", onVvScroll as any);
    // @ts-ignore - geometrychange is Safari-specific
    vv.addEventListener("geometrychange", onVvGeometry as any);
  }

  // Late UI chrome adjustments (address bar hide/show)
  [0, 250, 800].forEach((ms) => {
    timeouts.push(window.setTimeout(schedule, ms));
  });

  // Disposer
  return () => {
    window.removeEventListener("resize", onResize);
    window.removeEventListener("orientationchange", onOrientation);
    const vvLocal = window.visualViewport ?? null;
    if (vvLocal) {
      vvLocal.removeEventListener("resize", onVvResize);
      vvLocal.removeEventListener("scroll", onVvScroll as any);
      // @ts-ignore - geometrychange is Safari-specific
      vvLocal.removeEventListener("geometrychange", onVvGeometry as any);
    }
    timeouts.forEach(clearTimeout);
    if (raf) cancelAnimationFrame(raf);
  };
}
